import React from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, IconButton, Stack, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AimOutlined,
  CalendarOutlined,
  CaretDownOutlined,
  ClockCircleOutlined,
  FormOutlined,
  HourglassOutlined,
  LineChartOutlined,
  MessageOutlined,
  StarOutlined,
  ToolOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import logoIcon from 'assets/images/logo-icon.png';
import logoIconDark from 'assets/images/logo-icon-dark.png';
import logoBoxBlack from 'assets/images/logobox_black.png';
import QuickStartWizard, { QUICK_START_KEYS, readQuickStartState } from './QuickStartWizard';
import { getRedesignThemeTokens } from './redesignTheme';

const NAV_ITEMS = [
  { label: 'Open Trades', path: '/tradingcycle', icon: LineChartOutlined },
  { label: 'Journal', path: '/journal', icon: CalendarOutlined },
  { label: 'Performance', path: '/performance', icon: FormOutlined },
  { label: 'Risk Management', path: '/risk-management', icon: HourglassOutlined },
  { label: 'Tool Box', path: '/toolbox', icon: ToolOutlined }
];
const BOTTOM_NAV_ITEMS = [{ label: 'Trading Challenge', path: '/trading-challenge', icon: TrophyOutlined }];

const RAIL_WIDTH = 58;
const iconSlot = { width: RAIL_WIDTH, minWidth: RAIL_WIDTH, display: 'flex', alignItems: 'center', justifyContent: 'center' };

// Keep the normal brand treatment at rest, but use the dedicated black mark
// against the light-blue hover surface in dark mode.
function BrandMark({ dark, activeLight }) {
  const baseLogo = dark && !activeLight ? logoIconDark : logoIcon;
  return (
    <Box sx={{ position: 'relative', width: 35, height: 35, display: 'inline-flex', flex: '0 0 35px' }}>
      <Box component="img" className="tt-brand-base" src={baseLogo} alt="Tradatomy" sx={{ width: 35, height: 35, transition: 'opacity 150ms ease' }} />
      {dark ? <Box component="img" className="tt-brand-hover" src={logoBoxBlack} alt="" aria-hidden="true" sx={{ position: 'absolute', top: 0, left: 0, width: 35, height: 35, opacity: 0, transition: 'opacity 150ms ease' }} /> : null}
    </Box>
  );
}
BrandMark.propTypes = { dark: PropTypes.bool, activeLight: PropTypes.bool };

export default function RedesignShell({ children, rightRail, rightRailWidth = 366 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const compact = useMediaQuery('(max-width:1100px)');
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [quickStartOpen, setQuickStartOpen] = React.useState(false);
  const [quickStartState, setQuickStartState] = React.useState(readQuickStartState);

  const quickStartRemaining = QUICK_START_KEYS.filter((key) => !quickStartState[key]).length;
  const openQuickStart = () => {
    setMenuOpen(false);
    setQuickStartState(readQuickStartState());
    setQuickStartOpen(true);
  };

  const go = (path) => { setMenuOpen(false); navigate(path); };
  const navRows = (expanded = false, items = NAV_ITEMS) => (
    <Stack spacing={0.5} sx={{ width: '100%' }}>
      {items.map(({ label, path, icon: Icon }) => {
        const active = location.pathname === path;
        return (
          <Box
            key={path}
            component="button"
            type="button"
            aria-label={label}
            onClick={() => go(path)}
            sx={{
              width: '100%',
              height: 40,
              border: 0,
              boxShadow: active && !expanded ? 'inset -3px 0 0 #01A8F8' : 'none',
              borderRadius: expanded ? '0 12px 12px 0' : active ? 0 : '8px',
              bgcolor: active ? 'var(--app-selected)' : 'transparent',
              color: '#01A8F8',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              p: 0,
              textAlign: 'left',
              '&:hover': { bgcolor: active ? 'var(--app-selected)' : 'var(--app-hover)' }
            }}
          >
            <Box sx={iconSlot}><Icon style={{ fontSize: 19 }} /></Box>
            {expanded ? <Typography sx={{ fontSize: 14.5, fontWeight: active ? 700 : 500, color: active ? '#01A8F8' : 'var(--app-text)' }}>{label}</Typography> : null}
          </Box>
        );
      })}
    </Stack>
  );

  const brandActiveLight = !dark && location.pathname === '/dashboard';
  const brandSwapSx = dark ? { '&:hover .tt-brand-base': { opacity: 0 }, '&:hover .tt-brand-hover': { opacity: 1 }, '&:hover .tt-brand-word': { color: '#252F40' } } : {};
  const railBorder = dark ? '1px solid #2a323d' : '1px solid #E6EBF1';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: dark ? '#0d1117' : '#eceef1', p: { xs: 0, md: '26px' }, ...getRedesignThemeTokens(dark), color: 'var(--app-text)' }}>
      <Box sx={{ minHeight: { xs: '100vh', md: 'calc(100vh - 52px)' }, bgcolor: 'var(--app-surface)', borderRadius: { xs: 0, md: '30px' }, overflow: 'clip', boxShadow: { md: dark ? '0 24px 60px -28px rgba(0,0,0,.8)' : '0 24px 60px -28px rgba(28,38,58,.28), 0 2px 8px rgba(28,38,58,.05)' }, display: 'flex', position: 'relative', '& main': { bgcolor: 'var(--app-surface)', color: 'var(--app-text)' }, '& aside': { bgcolor: 'var(--app-surface-rail) !important', color: 'var(--app-text)' } }}>
        <Box sx={{ width: RAIL_WIDTH, minWidth: RAIL_WIDTH, borderRight: railBorder, display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', alignItems: 'center', zIndex: 42 }}>
          <Tooltip title="Dashboard" placement="right"><Box component="button" type="button" aria-label="Dashboard home" onClick={() => go('/dashboard')} sx={{ width: '100%', height: 64, p: 0, border: 0, borderBottom: railBorder, bgcolor: location.pathname === '/dashboard' ? 'var(--app-selected)' : 'transparent', display: 'grid', placeItems: 'center', cursor: 'pointer', '&:hover': { bgcolor: '#E6F7FF' }, ...brandSwapSx }}><BrandMark dark={dark} activeLight={brandActiveLight} /></Box></Tooltip>
          <Tooltip title="New Cycle" placement="right"><IconButton aria-label="New Cycle" onClick={() => go('/tradingcycle')} sx={{ width: 36, height: 36, borderRadius: '11px', bgcolor: '#01A8F8', color: '#fff', my: '16px', boxShadow: '0 1px 4px rgba(0,0,0,.1)', '&:hover': { bgcolor: '#FFCE6F', color: '#252F40' } }}><AimOutlined /></IconButton></Tooltip>
          {navRows(false)}
          <Box sx={{ flex: 1 }} />
          {navRows(false, BOTTOM_NAV_ITEMS)}
          <Tooltip title="Feedback" placement="right"><IconButton aria-label="Feedback" onClick={() => go('/contact-us')} sx={{ width: 40, height: 36, borderRadius: '9px', color: '#01A8F8', '&:hover': { bgcolor: 'var(--app-selected)', color: '#01A8F8' } }}><MessageOutlined style={{ fontSize: 18 }} /></IconButton></Tooltip>
          <Tooltip title="Discussions" placement="right"><IconButton aria-label="Discussions" onClick={() => go('/contact-us')} sx={{ width: 40, height: 36, borderRadius: '9px', color: '#01A8F8', '&:hover': { bgcolor: 'var(--app-selected)', color: '#01A8F8' } }}><StarOutlined style={{ fontSize: 18 }} /></IconButton></Tooltip>
          <Tooltip title={`Quick Start · ${quickStartRemaining} left`} placement="right"><Box component="button" type="button" aria-label="Open Quick Start" onClick={openQuickStart} sx={{ width: '100%', height: 40, p: 0, border: 0, bgcolor: 'var(--app-selected)', color: '#01A8F8', boxShadow: 'inset -3px 0 0 #01A8F8', display: 'grid', placeItems: 'center', mb: 1.5, cursor: 'pointer' }}><ClockCircleOutlined style={{ fontSize: 19 }} /></Box></Tooltip>
        </Box>

        <Tooltip title={menuOpen ? 'Collapse menu' : 'Expand menu'} placement="right">
          <IconButton onClick={() => setMenuOpen((value) => !value)} aria-label={menuOpen ? 'Collapse navigation' : 'Expand navigation'} sx={{ position: 'absolute', left: menuOpen ? 284 : RAIL_WIDTH - 10, top: '50%', transform: 'translateY(-50%)', width: 20, height: 34, zIndex: 60, border: 0, borderRadius: '8px', bgcolor: 'transparent', color: '#8b96aa', transition: 'left 170ms ease, color 170ms ease, background-color 170ms ease', '&:hover': { bgcolor: 'var(--app-selected)', color: '#01A8F8' } }}><CaretDownOutlined style={{ fontSize: 11, transform: `rotate(${menuOpen ? 90 : -90}deg)`, transition: 'transform 170ms ease' }} /></IconButton>
        </Tooltip>

        <Box sx={{ position: 'absolute', inset: '0 auto 0 0', width: 296, zIndex: 50, bgcolor: 'var(--app-surface)', borderRight: '1px solid var(--app-border)', boxShadow: '8px 0 34px -12px rgba(28,38,58,.16)', opacity: menuOpen ? 1 : 0, pointerEvents: menuOpen ? 'auto' : 'none', transition: 'opacity 170ms ease-out', display: { xs: 'none', sm: 'flex' }, flexDirection: 'column' }}>
          <Box component="button" type="button" aria-label="Dashboard home" onClick={() => go('/dashboard')} sx={{ width: '100%', height: 64, p: 0, border: 0, borderBottom: railBorder, bgcolor: location.pathname === '/dashboard' ? 'var(--app-selected)' : 'transparent', display: 'flex', alignItems: 'center', cursor: 'pointer', textAlign: 'left', '&:hover': { bgcolor: '#E6F7FF' }, ...brandSwapSx }}><Box sx={iconSlot}><BrandMark dark={dark} activeLight={brandActiveLight} /></Box><Typography className="tt-brand-word" sx={{ fontSize: 19, fontWeight: 600, letterSpacing: '-.01em', color: 'var(--app-text)' }}>tradatomy</Typography></Box>
          <Box component="button" aria-label="New Cycle" onClick={() => go('/tradingcycle')} sx={{ height: 68, border: 0, bgcolor: 'transparent', p: 0, display: 'flex', alignItems: 'center', cursor: 'pointer' }}><Box sx={iconSlot}><Box sx={{ width: 36, height: 36, borderRadius: '11px', bgcolor: '#01A8F8', color: '#fff', display: 'grid', placeItems: 'center' }}><AimOutlined /></Box></Box><Typography sx={{ fontSize: 14, fontWeight: 700 }}>New Cycle</Typography></Box>
          {navRows(true)}
          <Box sx={{ flex: 1 }} />
          {navRows(true, BOTTOM_NAV_ITEMS)}
          {[
            ['Feedback', MessageOutlined, '/contact-us'],
            ['Discussions', StarOutlined, '/contact-us']
          ].map(([label, Icon, path]) => (
            <Box
              key={label}
              component="button"
              type="button"
              onClick={() => go(path)}
              sx={{
                width: '100%',
                height: 40,
                p: 0,
                border: 0,
                bgcolor: 'transparent',
                color: 'var(--app-text)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                textAlign: 'left',
                '&:hover': { bgcolor: 'var(--app-hover)' }
              }}
            >
              <Box sx={iconSlot}>
                <Icon style={{ color: 'var(--app-text-secondary)', fontSize: 18 }} />
              </Box>
              <Typography sx={{ fontSize: 14.5, color: 'var(--app-text)' }}>{label}</Typography>
            </Box>
          ))}
          <Box component="button" type="button" onClick={openQuickStart} sx={{ width: '100%', height: 54, display: 'flex', alignItems: 'center', mb: 1, p: 0, border: 0, bgcolor: 'transparent', color: 'var(--app-text)', cursor: 'pointer', textAlign: 'left', '&:hover': { bgcolor: 'var(--app-selected)' } }}><Box sx={iconSlot}><ClockCircleOutlined style={{ color: '#01A8F8', fontSize: 19 }} /></Box><Typography sx={{ fontSize: 14.5, fontWeight: 600 }}>Quick Start</Typography><Box sx={{ ml: 'auto', mr: 2, px: 1, py: .35, borderRadius: 99, bgcolor: 'var(--app-selected)', color: '#01A8F8', fontSize: 11, fontWeight: 700 }}>{quickStartRemaining} left</Box></Box>
        </Box>

        <Box component="main" sx={{ minWidth: 0, flex: 1, overflowX: 'hidden', overflowY: 'auto', maxHeight: { md: 'calc(100vh - 52px)' } }}>{children}</Box>
        {!compact && rightRail ? <Box component="aside" sx={{ width: rightRailWidth, minWidth: rightRailWidth, bgcolor: 'var(--app-surface-rail)', borderLeft: '1px solid var(--app-border)', overflowY: 'auto', maxHeight: 'calc(100vh - 52px)' }}>{rightRail}</Box> : null}
        <Box sx={{ width: { xs: 0, md: 11 }, minWidth: { xs: 0, md: 11 }, bgcolor: '#01A8F8' }} />
      </Box>
      <QuickStartWizard open={quickStartOpen} onClose={() => setQuickStartOpen(false)} onNavigate={navigate} onProgressChange={setQuickStartState} />
    </Box>
  );
}

RedesignShell.propTypes = { children: PropTypes.node.isRequired, rightRail: PropTypes.node, rightRailWidth: PropTypes.number };
